const SHADOW_PRESETS = {
  light: { shadow: '2px 0 5px -1px rgba(0,0,0,0.1)', width: '4px' },
  medium: { shadow: '8px 0 15px -4px rgba(0,0,0,0.35)', width: '10px' },
  strong: { shadow: '4px 0 5px -1px rgba(0,0,0,.16078)', width: '14px' },
};
const DEFAULT_SHADOW = SHADOW_PRESETS.medium;

let shadowStyleElement = null;

const getShadowStyleElement = () => {
  if (!shadowStyleElement) {
    shadowStyleElement = document.getElementById('sticky-shadow-style');
    if (!shadowStyleElement) {
      shadowStyleElement = document.createElement('style');
      shadowStyleElement.id = 'sticky-shadow-style';
      document.head.appendChild(shadowStyleElement);
    }
  }
  return shadowStyleElement;
};

const updateShadowStyle = (fixedCount, intensity) => {
  const { shadow, width } = SHADOW_PRESETS[intensity] || DEFAULT_SHADOW;
  const styleTag = getShadowStyleElement();
  styleTag.textContent = `
    table[data-sticky-count="${fixedCount}"] th:nth-child(${fixedCount})::after,
    table[data-sticky-count="${fixedCount}"] td:nth-child(${fixedCount})::after {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      width: ${width};
      height: 100%;
      box-shadow: ${shadow};
      pointer-events: none;
      z-index: 10;
    }
  `;
};

const getFixedColumnBackground = (table, contextEl) => {
  const sampleCell = table.querySelector('tbody td');
  if (!sampleCell) return '#ffffff';
  const bgColor = window.getComputedStyle(sampleCell).backgroundColor;
  const isTransparent = bgColor === 'rgba(0, 0, 0, 0)' || bgColor === 'transparent';
  if (isTransparent) {
    const isDarkTheme = contextEl.closest('.theme--dark') !== null;
    return isDarkTheme ? '#1e1e1e' : '#ffffff';
  }
  return bgColor;
};

const applyStickyToColumnSet = (cells, fixedCount, bgColor, isHeader) => {
  if (!cells.length) return;
  const firstRow = cells[0].parentElement;
  const offsets = [];
  let accumulated = 0;
  for (let i = 0; i < fixedCount; i += 1) {
    offsets[i] = accumulated;
    if (firstRow.children[i]) accumulated += firstRow.children[i].offsetWidth;
  }
  cells.forEach((cell) => {
    const idx = cell.cellIndex;
    if (idx < fixedCount) {
      cell.style.position = 'sticky';
      cell.style.left = `${offsets[idx]}px`;
      cell.style.zIndex = isHeader ? '3' : '2';
      cell.style.background = bgColor;
    } else {
      cell.style.position = '';
      cell.style.left = '';
    }
  });
};

const applyWidthLimit = (table, fixedCount, maxPercent, container) => {
  if (!maxPercent || maxPercent <= 0) return false;
  for (let i = 0; i < fixedCount; i += 1) {
    const bodyCells = table.querySelectorAll(`tbody tr td:nth-child(${i + 1})`);
    bodyCells.forEach((cell) => { cell.style.width = ''; });
    const headerCell = table.querySelector(`thead th:nth-child(${i + 1})`);
    if (headerCell) headerCell.style.width = '';
  }
  // force reflow
  document.body.offsetHeight; // eslint-disable-line no-unused-expressions
  const { width: containerWidth } = container.getBoundingClientRect();
  if (!containerWidth) return false;
  const firstRowCells = table.querySelectorAll('tbody tr:first-child td');
  let totalFixedWidth = 0;
  const fixedColumnsData = [];
  for (let i = 0; i < fixedCount && i < firstRowCells.length; i += 1) {
    const cell = firstRowCells[i];
    const { width } = cell.getBoundingClientRect();
    totalFixedWidth += width;
    fixedColumnsData.push({ index: i, width });
  }
  const maxAllowedWidth = containerWidth * (maxPercent / 100);
  if (totalFixedWidth <= maxAllowedWidth) return false;
  const ratio = maxAllowedWidth / totalFixedWidth;
  fixedColumnsData.forEach((col) => {
    const newWidth = col.width * ratio;
    const colIndex = col.index;
    const bodyCells = table.querySelectorAll(`tbody tr td:nth-child(${colIndex + 1})`);
    bodyCells.forEach((cell) => { cell.style.width = `${newWidth}px`; });
    const headerCell = table.querySelector(`thead th:nth-child(${colIndex + 1})`);
    if (headerCell) headerCell.style.width = `${newWidth}px`;
  });
  document.body.offsetHeight; // eslint-disable-line no-unused-expressions
  return true;
};

const resetDirectiveStyles = (table) => {
  if (!table) return;
  table.removeAttribute('data-sticky-count');
  table.querySelectorAll('th, td').forEach((cell) => {
    const { style = {} } = cell;
    style.position = '';
    style.left = '';
    style.background = '';
    style.width = '';
  });
};

const createDirectiveController = (el, binding) => {
  const {
    count: fixedCount = 1,
    maxWidthPercent: rawMaxWidthPercent,
    shadowIntensity = 'medium',
  } = binding.value || {};
  const maxWidthPercent = rawMaxWidthPercent === undefined ? null : rawMaxWidthPercent;
  const table = el.querySelector('table');
  if (!table) return null;
  const scrollContainer = el.querySelector('.v-data-table__wrapper')
    || el.querySelector('.v-data-table')
    || el.parentElement;
  if (!scrollContainer) return null;
  table.setAttribute('data-sticky-count', fixedCount);
  updateShadowStyle(fixedCount, shadowIntensity);
  let isUpdating = false;
  let updateTimer = null;
  const fullUpdate = () => {
    if (isUpdating) return;
    isUpdating = true;
    try {
      const bgColor = getFixedColumnBackground(table, el);
      const headers = table.querySelectorAll('thead th');
      const bodyCells = table.querySelectorAll('tbody td');
      const resetLeftAndPosition = (cells) => {
        cells.forEach((c) => {
          c.style.left = '';
          c.style.position = '';
        });
      };
      resetLeftAndPosition(headers);
      resetLeftAndPosition(bodyCells);
      applyWidthLimit(table, fixedCount, maxWidthPercent, scrollContainer);
      applyStickyToColumnSet(headers, fixedCount, bgColor, true);
      applyStickyToColumnSet(bodyCells, fixedCount, bgColor, false);
    } finally {
      isUpdating = false;
    }
  };
  const debouncedFullUpdate = () => {
    if (updateTimer) clearTimeout(updateTimer);
    updateTimer = setTimeout(() => {
      fullUpdate();
      updateTimer = null;
    }, 20);
  };
  let resizeTimer = null;
  const onWindowResize = () => {
    if (resizeTimer) clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => debouncedFullUpdate(), 100);
  };
  window.addEventListener('resize', onWindowResize);
  const bodyObserver = new MutationObserver(() => debouncedFullUpdate());
  bodyObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
  const domObserver = new MutationObserver((mutations) => {
    if (isUpdating) return;
    const needsUpdate = mutations.some((m) => m.type === 'childList'
      || (m.type === 'attributes' && m.attributeName === 'class'));
    if (needsUpdate) debouncedFullUpdate();
  });
  domObserver.observe(el, { attributes: true, attributeFilter: ['class'] });
  domObserver.observe(table, { childList: true, subtree: true, attributes: true });
  fullUpdate();
  return {
    cleanup: () => {
      window.removeEventListener('resize', onWindowResize);
      if (resizeTimer) clearTimeout(resizeTimer);
      bodyObserver.disconnect();
      domObserver.disconnect();
      if (updateTimer) clearTimeout(updateTimer);
      resetDirectiveStyles(table);
    },
    params: {
      count: fixedCount,
      maxWidthPercent,
      shadowIntensity,
    },
  };
};

export default {
  inserted(el, binding) {
    if (el.stickyColumnsData) el.stickyColumnsData.cleanup();
    el.stickyColumnsData = createDirectiveController(el, binding);
  },
  update(el, binding) {
    const old = el.stickyColumnsData ? el.stickyColumnsData.params : null;
    const neu = binding.value || {};
    if (
      old
      && old.count === neu.count
      && old.maxWidthPercent === neu.maxWidthPercent
      && old.shadowIntensity === neu.shadowIntensity
    ) {
      return;
    }
    if (el.stickyColumnsData) el.stickyColumnsData.cleanup();
    el.stickyColumnsData = createDirectiveController(el, binding);
  },
  unbind(el) {
    if (el.stickyColumnsData) {
      el.stickyColumnsData.cleanup();
      delete el.stickyColumnsData;
    }
  },
};
